import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { PlanSubmission } from "src/app/generated-models/PlanSubmission";
import { ApiService } from "src/app/services";
import { IUserProfile, PlanAssignResultEnum, PlanReconfirmationTypeEnum, PlanValidationErrorCode, altDocUrl } from "src/app/npr-request.model";
import { ModalService } from "src/app/shared/modal.service";
import { ToastrService } from "ngx-toastr";
import { fillPlanAssignmentWarningMessageFromException } from "../../../../utility/common-helper";

@Component({
  standalone: false,
  templateUrl: "./plan-assignment-response-modal.component.html",
  styleUrls: ["../../../../styles.scss"],
})
export class PlanAssignmentResponseModalComponent implements OnInit, OnDestroy {
  @Input() planSubmissions: PlanSubmission[];

  public aggregatedResult: string[] = [];
  public aggregatedResultTemp: string[] = [];
  public isCompleted = false;
  public requireSkuAvailableInCrp = true;
  public hasVmSkuPslWarning = false;
  public ignoreVmSkusNotInPslWarning = false;
  public isPartiallyFulfilled = false;
  public retried = false;
  public isAdmin = false;
  private isDestroyed = false;
  private userProfile: IUserProfile;

  constructor(
    public activeModal: NgbActiveModal,
    protected apiService: ApiService,
    protected modalService: ModalService,
    private notificationService: ToastrService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) { }

  async ngOnInit() {
    // Get user profile to determine admin status
    this.userProfile = await this.apiService.getUserProfile();
    this.isAdmin = this.userProfile?.IsAdmin ?? false;
    
    await this.assignPlans();
  }

  ngOnDestroy() {
    this.isDestroyed = true;
  }

  async assignPlans() {
    this.ngZone.run(() => {
      this.isCompleted = false;
      this.isPartiallyFulfilled = false;
      this.aggregatedResult = [];
      this.aggregatedResultTemp = [];
      this.cdr.detectChanges();
    });

    // remove invalid plan submissions. i.e: plans with same region and service in minimum and GA name are assigned at the same time
    var filteredPlanSubmissions: PlanSubmission[] = [];
    this.planSubmissions.forEach((plan: PlanSubmission) => {
      var hasSamePlanWithGA = this.planSubmissions.some(
        (data: PlanSubmission) => data.Region === plan.Region && data.ServiceTreeId === plan.ServiceTreeId && data.Blueprint === "GA"
      );
      if (!(plan.Blueprint === "Minimum" && hasSamePlanWithGA)) {
        filteredPlanSubmissions.push(plan);
      }
    });
    this.planSubmissions = filteredPlanSubmissions;

    for (const planSubmission of this.planSubmissions) {
      try {
        const requireSkuAvailableInCrpSnapshot = this.requireSkuAvailableInCrp;
        planSubmission.RequireSkuAvailableInCrp = requireSkuAvailableInCrpSnapshot;
        planSubmission.IgnoreVmSkusNotInPslWarning = this.ignoreVmSkusNotInPslWarning;

        var response = await this.apiService.assignPlanToRegion(planSubmission, this.ignoreVmSkusNotInPslWarning).toPromise();
        var isPartiallyFulfilled = response?.IsPartiallyFulfilled === true;

        if (response?.Result == PlanAssignResultEnum.Warning) {
          this.notificationService.warning(`Warning: ${response.Message}`);
          this.isPartiallyFulfilled = isPartiallyFulfilled;
        }

        if (isPartiallyFulfilled) {
          var r = await this.modalService.planAssignmentReconfirmModal(
            planSubmission,
            PlanReconfirmationTypeEnum.PartiallyFulfilled,
            response?.Message ?? ""
          );

          var assignPlanMessage = `Assign plan from <b>${planSubmission.ServiceTeam}</b>, version <b>${planSubmission.Version}</b> with name <b>${planSubmission.Blueprint}</b> to region <b>${planSubmission.Region}</b> `;
          if (r == "cancelled") {
            this.aggregatedResultTemp.push(assignPlanMessage + "cancelled.");
          } else if (r == "successful") {
            this.aggregatedResultTemp.push(assignPlanMessage + "successfully. Only net new subscriptions will trigger capacity order and approval.");
          } else {
            if (typeof r === "string" && r.includes("Plan validation failed")) {
              this.requireSkuAvailableInCrp = false;
            }

            this.aggregatedResultTemp.push(assignPlanMessage + `failed with error : ${r}`);
          }
        } else {
          this.aggregatedResultTemp.push(
            `Assign blueprint <b>${planSubmission.Blueprint}</b> for <b>${planSubmission.ServiceTreeId}</b> to region <b>${planSubmission.Region}</b> successfully with message: <br/>${response?.Message}`
          );
        }
      } catch (ex) {
        var warningMessage = "";
        if (ex?.error?.Code == PlanValidationErrorCode.PlanValidationCrpSkuNotAvailable) {
          this.requireSkuAvailableInCrp = false;
          let instanceCount = ex?.error?.CrpSkuNotAvailableInstances?.length ?? 0;
          let familyCount = ex?.error?.CrpSkuNotAvailableFamilies?.length ?? 0;
          let deprecatedCount = ex?.error?.CrpSkuDeprecated?.length ?? 0;
          if (!this.retried) {
            warningMessage = fillPlanAssignmentWarningMessageFromException(ex, warningMessage);
            if (deprecatedCount > 0) {
              warningMessage += `<b>Close</b> and remove the deprecated VM SKUs before re-assigning.<br/> Or <b>[Proceed]</b> to acknowledge the risk and continue assigning with the current plan.<br/>`;
            }
            else if (instanceCount > 0 || familyCount > 0) {
              warningMessage += `<b>Close</b> and add some <a href="${altDocUrl}" target="_blank" >alternatives</a> available in the region before re-assigning.<br/> Or <b>[Proceed]</b> to acknowledge the risk and continue assigning with the current plan.<br/>`;
            }
          }
        } else if (ex?.error?.Code == PlanValidationErrorCode.PlanValidationVmSkusNotInPsl) {
          this.hasVmSkuPslWarning = true;
          let vmSkusCount = ex?.error?.VmSkusNotInPsl?.length ?? 0;
          if (!this.retried) {
            warningMessage = ex?.error.Message ?? "VM SKUs not found in regional PSL.";
            if (vmSkusCount > 0) {
              warningMessage += `<br/>VM SKUs: ${ex.error.VmSkusNotInPsl.join(", ")}<br/>`;
              if (this.isAdmin) {
                warningMessage += `<b>[Proceed]</b> to acknowledge the risk and continue assigning with the current plan.<br/>`;
              } else {
                warningMessage += `Please contact an administrator to review and approve this plan assignment.<br/>`;
              }
            }
          }
        } else if (ex?.error.Message != null) {
          warningMessage = ex?.error.Message;
        }
        this.aggregatedResultTemp.push(
          `Assign blueprint <b>${planSubmission.Blueprint}</b> for <b>${planSubmission.ServiceTreeId}</b> to region <b>${planSubmission.Region}</b> failed with error:<br/> <font color="red">${warningMessage}</font>`
        );
      }

      this.ngZone.run(() => {
        if (!this.isDestroyed) {
          this.aggregatedResult = [...this.aggregatedResultTemp];
          this.cdr.detectChanges();
        }
      });
    }

    this.ngZone.run(() => {
      if (!this.isDestroyed) {
        this.aggregatedResult = [...this.aggregatedResultTemp];
        this.isCompleted = true;
        this.cdr.detectChanges();
      }
    });
  }

  close() {
    this.activeModal.close(true);
  }

  async continue() {
    if (this.hasVmSkuPslWarning && this.isAdmin)
    {
      this.ignoreVmSkusNotInPslWarning = true;
    }

    this.retried = true;
    this.aggregatedResult = [];
    this.aggregatedResultTemp = [];
    await this.assignPlans();
  }
}
